import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import toast from "react-hot-toast";
import {
  listGirvis,
  showGirvi,
  listGirviBanks,
  isErpApiSuccess,
  postGirviPaymentEntry,
} from "../../erpClient";
import {
  normalizeGirviBanksListResponse,
  normalizeListGirvisRows,
  getGirviShowFirstObject,
  isGirviApiSuccess,
} from "../../utils/girviErpAdapter";
import { GIRVI_THEME } from "./girviTheme";
import { formatIndianRupee } from "../../utils/formatLiveRate";
import "./girvi.css";

const AVAILABLE_PAYMENT_METHODS = [
  "cash",
  "cheque",
  "bank_transfer",
  "upi",
  "card",
  "wallet",
];

const PAYMENT_METHOD_LABELS = {
  cash: "Cash",
  cheque: "Cheque",
  bank_transfer: "Bank Transfer",
  upi: "UPI",
  card: "Card",
  wallet: "Wallet",
};

function GirviPaymentEntry() {
  const navigate = useNavigate();

  const [girviOptions, setGirviOptions] = useState([]);
  const [selectedGirviOption, setSelectedGirviOption] = useState(null);
  const [girviData, setGirviData] = useState(null);
  const [loadingGirvi, setLoadingGirvi] = useState(false);
  const [saving, setSaving] = useState(false);

  const [paymentDate, setPaymentDate] = useState(
    new Date().toISOString().split("T")[0],
  );
  const [paymentAmount, setPaymentAmount] = useState("");
  const [payMode, setPayMode] = useState("cash");
  const [bankType, setBankType] = useState("");
  const [remarks, setRemarks] = useState("");
  const [paymentTypes, setPaymentTypes] = useState([]);

  // Pageload: only girvi_bank
  useEffect(() => {
    void fetchGirviBanks();
  }, []);

  useEffect(() => {
    const handleApiUrlChange = () => {
      void fetchGirviBanks();
      setGirviOptions([]);
      setSelectedGirviOption(null);
      setGirviData(null);
      setPaymentAmount("");
    };

    window.addEventListener("apiUrlChanged", handleApiUrlChange);
    return () => {
      window.removeEventListener("apiUrlChanged", handleApiUrlChange);
    };
  }, []);

  const fetchGirviBanks = async () => {
    try {
      const response = await listGirviBanks({});
      const rows = normalizeGirviBanksListResponse(response);
      if (rows.length && isGirviApiSuccess(response)) {
        setPaymentTypes(
          rows.map((bank) => ({
            value: String(bank.id),
            label:
              bank.bank_name ||
              bank.account_number ||
              bank.account_type ||
              `Bank ${bank.id}`,
          })),
        );
      } else {
        setPaymentTypes([]);
      }
    } catch (error) {
      console.error("Error fetching girvi banks:", error);
      setPaymentTypes([]);
    }
  };

  const fetchGirviList = async (search = "") => {
    const trimmed = String(search || "").trim();
    if (trimmed.length < 3) {
      setGirviOptions([]);
      return [];
    }

    try {
      const response = await listGirvis({
        search: trimmed,
        page: 1,
        limit: 10,
      });

      if (isGirviApiSuccess(response)) {
        const rows = normalizeListGirvisRows(response);
        const options = rows.map((item) => ({
          value: String(item.id ?? item.girvi_document_id ?? item.girvi_id),
          label: `${item.girvi_number || "N/A"} - ${
            item.customer_name || "Unknown"
          } (${item.customer_mobile || "-"})`,
          data: item,
        }));
        setGirviOptions(options);
        return options;
      }
      setGirviOptions([]);
      return [];
    } catch (error) {
      console.error("Error searching girvi:", error);
      setGirviOptions([]);
      return [];
    }
  };

  const handleGirviSearchInput = (inputValue) => {
    const trimmed = String(inputValue || "").trim();
    if (trimmed.length < 3) {
      setGirviOptions([]);
      return;
    }
    void fetchGirviList(trimmed);
  };

  const handleGirviSelect = async (option) => {
    setSelectedGirviOption(option);
    setGirviData(null);
    setPaymentAmount("");

    if (!option?.value) return;

    try {
      setLoadingGirvi(true);
      const response = await showGirvi({ id: Number(option.value) });
      const doc =
        getGirviShowFirstObject(response) ??
        response?.data?.data ??
        response?.data;

      if (isGirviApiSuccess(response) || isErpApiSuccess(response)) {
        setGirviData(doc);
        const remaining =
          doc?.summary?.remaining ??
          doc?.remaining_amount ??
          option?.data?.remaining_amount ??
          0;
        setPaymentAmount(
          Number(remaining) > 0 ? Number(remaining).toFixed(2) : "",
        );
      } else {
        toast.error("Failed to load Girvi details");
      }
    } catch (error) {
      console.error("Error fetching Girvi details:", error);
      toast.error("Failed to load Girvi details");
    } finally {
      setLoadingGirvi(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const girviId =
      selectedGirviOption?.value ||
      girviData?.girvi_details?.id ||
      girviData?.id;

    if (!girviId) {
      toast.error("Please search and select a Dihran first");
      return;
    }

    if (!paymentAmount || parseFloat(paymentAmount) <= 0) {
      toast.error("Please enter a valid payment amount");
      return;
    }

    const remaining =
      girviData?.summary?.remaining ??
      girviData?.remaining_amount ??
      selectedGirviOption?.data?.remaining_amount ??
      0;
    if (parseFloat(paymentAmount) > Number(remaining)) {
      toast.error(
        `Payment amount cannot exceed remaining balance: ₹${Number(
          remaining,
        ).toFixed(2)}`,
      );
      return;
    }

    if (
      (payMode === "bank_transfer" ||
        payMode === "cheque" ||
        payMode === "upi") &&
      !bankType
    ) {
      toast.error("Please select a bank account for this payment method");
      return;
    }

    setSaving(true);

    try {
      const byUser = JSON.parse(localStorage.getItem("user"))?.id || 1;

      const data = await postGirviPaymentEntry({
        girvi_id: Number(girviId),
        by_user: byUser,
        payment: {
          amount: parseFloat(paymentAmount),
          pay_mode: PAYMENT_METHOD_LABELS[payMode] || payMode,
          bank_type: bankType ? parseInt(bankType, 10) : 0,
          remarks,
          payment_date: paymentDate,
        },
      });

      if (isErpApiSuccess(data) || data?.message === "radha") {
        toast.success("Payment recorded successfully!");
        await handleGirviSelect(selectedGirviOption);
        setRemarks("");
        setBankType("");
      } else {
        toast.error(data?.message || "Failed to record payment");
      }
    } catch (error) {
      console.error("Error recording payment:", error);
      toast.error(
        error.response?.data?.message ||
          error.message ||
          "Failed to record payment",
      );
    } finally {
      setSaving(false);
    }
  };

  const summary = girviData?.summary || {};
  const detail = girviData?.girvi_details || girviData?.detail || girviData || {};
  const remaining =
    summary.remaining ??
    girviData?.remaining_amount ??
    selectedGirviOption?.data?.remaining_amount ??
    0;
  const remainingNum = Number(remaining) || 0;

  return (
    <>
      <style>
        {`
          .girvi-customer-dropdown .dropdown-item:hover,
          .girvi-customer-dropdown .dropdown-item.active {
            background-color: ${GIRVI_THEME.bgColor} !important;
            color: ${GIRVI_THEME.textColor} !important;
          }
        `}
      </style>
      <div
        className="body-wrapper"
        style={{
          marginLeft: 0,
          backgroundColor: "#f5f5f5",
          minHeight: "100vh",
          paddingTop: "20px",
        }}
      >
        <div className="container-fluid px-4 py-3">
          <div className="row mb-4">
            <div className="col-12">
              <div
                className="rounded-3 p-4 d-flex align-items-center justify-content-between"
                style={{
                  background: GIRVI_THEME.bgGradient,
                  boxShadow: GIRVI_THEME.shadow,
                }}
              >
                <div>
                  <h3 className="text-white mb-1 fw-bold">
                    Monthly Payment Entry
                  </h3>
                  <p
                    className="text-white mb-0 opacity-75"
                    style={{ fontSize: "0.9rem" }}
                  >
                    Search a customer / Dihran, then record payment
                  </p>
                </div>
                <button
                  className="btn"
                  style={{
                    backgroundColor: "#fff",
                    color: GIRVI_THEME.bgColor,
                    border: "none",
                    borderRadius: "8px",
                    padding: "12px 24px",
                    fontWeight: "600",
                  }}
                  onClick={() => navigate("/girvitable")}
                >
                  Back to List
                </button>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-12 col-lg-8">
              <div className="card girvi-card mb-4">
                <div className="card-header girvi-card-header">
                  <h6 className="mb-0 text-white">Search User / Dihran</h6>
                </div>
                <div className="card-body p-4">
                  <Select
                    options={girviOptions}
                    value={selectedGirviOption}
                    onChange={handleGirviSelect}
                    onInputChange={(inputValue, meta) => {
                      if (meta.action === "input-change") {
                        handleGirviSearchInput(inputValue);
                      }
                      return inputValue;
                    }}
                    placeholder="Search mobile / name / Dihran no..."
                    isSearchable
                    isClearable
                    filterOption={() => true}
                    noOptionsMessage={({ inputValue }) =>
                      String(inputValue || "").trim().length < 3
                        ? "Type at least 3 characters to search"
                        : "No Dihran found"
                    }
                  />
                </div>
              </div>

              {loadingGirvi ? (
                <div className="text-center py-5">
                  <div className="girvi-spinner" />
                </div>
              ) : girviData ? (
                <>
                  <div className="card girvi-card mb-4">
                    <div className="card-header girvi-card-header">
                      <h6 className="mb-0 text-white">Customer Information</h6>
                    </div>
                    <div className="card-body p-4">
                      <div className="row">
                        <div className="col-md-6 mb-3">
                          <label className="girvi-form-label">
                            Customer Name
                          </label>
                          <input
                            type="text"
                            className="form-control girvi-form-control"
                            value={
                              detail.customer_name ||
                              selectedGirviOption?.data?.customer_name ||
                              "N/A"
                            }
                            readOnly
                          />
                        </div>
                        <div className="col-md-6 mb-3">
                          <label className="girvi-form-label">
                            Mobile Number
                          </label>
                          <input
                            type="text"
                            className="form-control girvi-form-control"
                            value={
                              detail.customer_mobile ||
                              selectedGirviOption?.data?.customer_mobile ||
                              "N/A"
                            }
                            readOnly
                          />
                        </div>
                        <div className="col-md-6 mb-3">
                          <label className="girvi-form-label">
                            Principal Amount
                          </label>
                          <input
                            type="text"
                            className="form-control girvi-form-control"
                            value={formatIndianRupee(
                              summary.principal ??
                                detail.principal_given ??
                                selectedGirviOption?.data?.principal_given ??
                                0,
                            )}
                            readOnly
                          />
                        </div>
                        <div className="col-md-6 mb-3">
                          <label className="girvi-form-label">
                            Remaining Amount
                          </label>
                          <input
                            type="text"
                            className="form-control girvi-form-control fw-bold"
                            style={{
                              color:
                                remainingNum > 0 ? "#dc3545" : "#28a745",
                            }}
                            value={formatIndianRupee(remainingNum)}
                            readOnly
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="card girvi-card">
                    <div className="card-header girvi-card-header">
                      <h6 className="mb-0 text-white">Payment Entry</h6>
                    </div>
                    <div className="card-body p-4">
                      <form onSubmit={handleSubmit}>
                        <div className="row">
                          <div className="col-md-6 mb-3">
                            <label className="girvi-form-label">
                              Payment Date
                            </label>
                            <input
                              type="date"
                              className="form-control girvi-form-control"
                              value={paymentDate}
                              onChange={(e) => setPaymentDate(e.target.value)}
                              required
                            />
                          </div>
                          <div className="col-md-6 mb-3">
                            <label className="girvi-form-label">
                              Payment Amount
                            </label>
                            <input
                              type="number"
                              className="form-control girvi-form-control"
                              placeholder="0.00"
                              value={paymentAmount}
                              onChange={(e) => setPaymentAmount(e.target.value)}
                              step="0.01"
                              min="0.01"
                              max={remainingNum}
                              required
                            />
                          </div>
                          <div className="col-md-6 mb-3">
                            <label className="girvi-form-label">
                              Payment Mode
                            </label>
                            <select
                              className="form-select girvi-form-control"
                              value={payMode}
                              onChange={(e) => {
                                setPayMode(e.target.value);
                                if (
                                  e.target.value === "cash" ||
                                  e.target.value === "wallet"
                                ) {
                                  setBankType("");
                                }
                              }}
                              required
                            >
                              {AVAILABLE_PAYMENT_METHODS.map((method) => (
                                <option key={method} value={method}>
                                  {PAYMENT_METHOD_LABELS[method]}
                                </option>
                              ))}
                            </select>
                          </div>
                          {(payMode === "bank_transfer" ||
                            payMode === "cheque" ||
                            payMode === "upi") && (
                            <div className="col-md-6 mb-3">
                              <label className="girvi-form-label">
                                Bank Account
                              </label>
                              <select
                                className="form-select girvi-form-control"
                                value={bankType}
                                onChange={(e) => setBankType(e.target.value)}
                                required
                              >
                                <option value="">Select Bank Account</option>
                                {paymentTypes.map((bank) => (
                                  <option key={bank.value} value={bank.value}>
                                    {bank.label}
                                  </option>
                                ))}
                              </select>
                            </div>
                          )}
                          <div className="col-12 mb-3">
                            <label className="girvi-form-label">Remarks</label>
                            <textarea
                              className="form-control girvi-form-control"
                              rows="3"
                              placeholder="Enter payment remarks (optional)"
                              value={remarks}
                              onChange={(e) => setRemarks(e.target.value)}
                            />
                          </div>
                          <div className="col-12 d-flex gap-2 justify-content-end">
                            <button
                              type="button"
                              className="btn girvi-btn-secondary"
                              onClick={() => navigate("/girvitable")}
                            >
                              Cancel
                            </button>
                            <button
                              type="submit"
                              className="btn girvi-btn-primary"
                              disabled={saving || remainingNum <= 0}
                            >
                              {saving ? "Processing..." : "Record Payment"}
                            </button>
                          </div>
                        </div>
                      </form>
                    </div>
                  </div>
                </>
              ) : (
                <div className="alert alert-light border">
                  Search and select a Dihran to enter payment.
                </div>
              )}
            </div>

            <div className="col-12 col-lg-4">
              <div
                className="card girvi-card sticky-top"
                style={{ top: "80px" }}
              >
                <div className="card-header girvi-card-header">
                  <h6 className="mb-0 text-white">Payment History</h6>
                </div>
                <div className="card-body p-3">
                  {girviData?.payments?.length > 0 ? (
                    <div className="list-group list-group-flush">
                      {girviData.payments.map((payment, index) => (
                        <div
                          key={index}
                          className="list-group-item border-0 px-0 py-2"
                          style={{ backgroundColor: "transparent" }}
                        >
                          <div className="fw-semibold" style={{ color: GIRVI_THEME.bgColor }}>
                            {formatIndianRupee(
                            payment.jaavak_amt || payment.amount || 0,
                          )}
                          </div>
                          <div className="small text-muted">
                            {payment.pay_mode || "N/A"}
                          </div>
                          <div className="small text-muted">
                            {payment.created_at
                              ? new Date(
                                  payment.created_at,
                                ).toLocaleDateString()
                              : "N/A"}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center text-muted py-4">
                      <p className="mb-0">No payments recorded yet</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default GirviPaymentEntry;
